import React, { useRef, useState } from "react";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../dialog";
import { Button } from "../button";
import { Input } from "../input";
import { PhotoIcon } from "@heroicons/react/20/solid";

/**
 * Uploads an image to Firebase Storage and returns a markdown snippet with
 * the size encoded in the alt text as `![alt|size](url)`.
 *
 * Props:
 *   postId      - string: stable post id, used as the Storage folder
 *   buttonLabel - string: optional button text override
 *   onInsert    - (markdownSnippet: string) => void
 */
export default function BlogImagePicker({ postId, buttonLabel = "Insertar imagen", onInsert }) {
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [size, setSize] = useState("full");
  const [error, setError] = useState("");

  function handleClickPick() {
    setError("");
    setUploadedUrl("");
    setAlt("");
    setSize("full");
    setOpen(true);
    // Defer to let dialog mount before opening the native picker
    setTimeout(() => fileInputRef.current?.click(), 50);
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `blog/${postId}/image-${Date.now()}-${safeName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setUploadedUrl(url);
    } catch (err) {
      console.error("Blog image upload failed", err);
      setError("No se pudo subir la imagen.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleInsert() {
    if (!uploadedUrl) return;
    const cleanAlt = (alt || "").replace(/[|[\]()]/g, "").trim();
    const snippet = `![${cleanAlt}|${size}](${uploadedUrl})`;
    onInsert?.(snippet);
    setOpen(false);
  }

  return (
    <>
      <Button type="button" outline onClick={handleClickPick}>
        <PhotoIcon className="h-4 w-4" />
        {buttonLabel}
      </Button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      {open && (
        <Dialog open onClose={() => !uploading && setOpen(false)} size="md">
          <DialogTitle>Insertar imagen</DialogTitle>
          <DialogBody className="space-y-4">
            {uploading && (
              <p className="text-sm text-gray-600">Subiendo imagen...</p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            {!uploading && !uploadedUrl && !error && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full rounded-lg border-2 border-dashed border-gray-300 p-6 text-center text-sm text-gray-600 hover:border-gray-400"
              >
                Haz click para seleccionar una imagen
              </button>
            )}
            {uploadedUrl && (
              <>
                <div className="rounded-lg border border-gray-200 p-2">
                  <img
                    src={uploadedUrl}
                    alt="preview"
                    className="mx-auto max-h-48 rounded"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium">
                    Texto alternativo (alt / caption)
                  </label>
                  <Input
                    value={alt}
                    onChange={(e) => setAlt(e.target.value)}
                    placeholder="Boda en jardín"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium">
                    Tamaño
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { k: "small", label: "Pequeña" },
                      { k: "medium", label: "Mediana" },
                      { k: "large", label: "Grande" },
                      { k: "full", label: "Completa" },
                    ].map((s) => (
                      <label
                        key={s.k}
                        className={`cursor-pointer rounded-full px-4 py-1.5 text-sm transition ${
                          size === s.k
                            ? "bg-[#1b4d2e] font-semibold text-white shadow-sm"
                            : "bg-gray-100 text-gray-700 hover:text-[#1b4d2e]"
                        }`}
                      >
                        <input
                          type="radio"
                          name="imgsize"
                          value={s.k}
                          checked={size === s.k}
                          onChange={() => setSize(s.k)}
                          className="sr-only"
                        />
                        {s.label}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </DialogBody>
          <DialogActions>
            <Button
              plain
              onClick={() => setOpen(false)}
              disabled={uploading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleInsert}
              disabled={!uploadedUrl || uploading}
            >
              Insertar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
